import { AfterViewInit, Component, HostListener, Input, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";

import { AddEvent,  GridDataResult, GridModule,  RemoveEvent } from "@progress/kendo-angular-grid";
import { State } from "@progress/kendo-data-query";
import { forkJoin, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { AsyncPipe, NgIf } from "@angular/common";
import { CHECK_ICON_ACTIVE, CHECK_ICON_CHECKED, CHECK_ICON_XMARK } from "../../../../../../constants/constant";
import { ControlResizerOptions, ControlResizerService } from "../../../../../../services/framework";
import { AuthPermIdentifier, RequestUserPermission } from "../../../../../modules/fwcore";
import { NullableBooleanClassDirective } from "../../../../../modules/fwcore/directives/nullable-boolean-class.directive";
import { DatePreferencePipe } from "../../../../../modules/fwcore/pipes/date-preference.pipe";
import { AuthorizationService, ConfirmationDialogService } from "../../../../../modules/fwcore/services";
import { RequestRoleTemplate, RoleTemplate } from "../../../models";
import { RoleTemplateServiceGrid } from "../../../services";
import { AdminService } from "../../../services/admin.service";
import { RoleTemplateEditorComponent } from "../role-template-editor/role-template-editor.component";

@Component({
  selector: 'app-role-template-grid',
  templateUrl: './role-template-grid.component.html',
  styleUrls: ['./role-template-grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [GridModule, NgIf,   NullableBooleanClassDirective,  RoleTemplateEditorComponent, AsyncPipe, DatePreferencePipe]
})

export class RoleTemplateGridComponent implements OnInit, AfterViewInit, OnDestroy {

  /*-------------------------- Input / Outputs -------------------------*/

  @Input() public disableOnSizingLocal: boolean = false;

  /*-------------------------- public properties / methods -------------*/

  public sizeChange(options?: ControlResizerOptions) {

    var defaultOptions: ControlResizerOptions = new ControlResizerOptions();

    const effectiveOptions = { ...defaultOptions, ...options };

    this.heightGrid = this.controlResizer.calculateHeight(effectiveOptions);
  }

  /*-------------------------- Internal properties  --------------------*/

  protected editDataItem?: RoleTemplate;

  protected destroy$ = new Subject();

  protected heightGrid: number = 300;

  protected licActive?: boolean | null;

  protected view: Observable<GridDataResult>;

  protected isNew?: boolean;

  protected req: RequestRoleTemplate = {
    skip: 0
    , take: 15
  };

  protected reqPerm: RequestUserPermission = {

  }

  protected perms?: AuthPermIdentifier[] = [];

  /*-------------------------- State & permissions  -------------------*/

  protected state: State = {
    skip: 0
    , take: 15
    , sort: []
  }

  protected showAdd?: boolean | null = true;

  /*--------------------------- Constructor  --------------------------*/

  constructor(protected service: RoleTemplateServiceGrid
    , protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected dialogConfirm: ConfirmationDialogService
    , protected controlResizer: ControlResizerService
    , protected router: Router) {

    this.view = service;
  }

  /*--------------------------- ng  -----------------------------------*/

  ngOnInit(): void {

    this.reqPerm.route = this.router.url;
    this.service.query(this.req);

    forkJoin([
      this.serviceAuth.pagePermissionsGet$(this.reqPerm)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([permissions]) => {
        this.perms = permissions.data ?? [];
        //this.showAdd = this.perms?.some(i => i.code === "RoleTemplate-Add");
      });
  }

  ngAfterViewInit(): void {

    if (!this.disableOnSizingLocal)
      this.sizeChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  -----------------------*/

  @HostListener('window:resize', ['$event'])
  protected onResize(event: Event) {
    if (!this.disableOnSizingLocal)
      this.sizeChange();
  }

  // -------------- Grid processing ------------------------------------------------

  protected onStateChange(state: State): void {

    this.state = state;

    this.req.skip = this.state.skip;
    this.req.take = this.state.take;

    this.service.query(this.req);
  }

  protected onAddRoleTemplate(): void {

    let item = new RoleTemplate();
    item.roleType = "CLI";
    item.isActive = true;

    this.isNew = true;
    this.editDataItem = item;

  }

  protected onEditRoleTemplate(args: AddEvent): void {

    this.isNew = false;
    this.editDataItem = args.dataItem;
  }

  protected onDeleteRoleTemplate(args: RemoveEvent) {

    const msg: string = "Are you sure you want to delete '" + args.dataItem.name + "' ?";

    this.dialogConfirm.open('Confirm Deletion', msg)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result.value)
          this.service.remove(args.dataItem);
      });

  }

  protected onCancelRoleTemplate(): void {
    this.editDataItem = undefined;
  }

  protected onSaveRoleTemplate(item: RoleTemplate): void {

    if (item.id) {

      let merged = { ...this.editDataItem, ...item };
      this.service.save([merged])
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
    else
      this.service.save([item])
        .pipe(takeUntil(this.destroy$))
        .subscribe();

    this.editDataItem = undefined;
  }

  /*--------------------------- internal functions  -------------------*/

  protected checkBox(checked: boolean): string {
    return checked ? CHECK_ICON_CHECKED : CHECK_ICON_XMARK;
  }

  protected getActiveClass(item: RoleTemplate): string {
    return item.isActive ? CHECK_ICON_ACTIVE : CHECK_ICON_XMARK;
  }

}
